import { ExportButton } from '@/common/buttons/ExportButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { KrCard } from '@/common/card/KrCard';
import { useDialog } from '@/common/dialog/DialogContext';
import { ContentBox } from '@/common/layout/ContentBox';
import { Spacer, SpacerLayout } from '@/common/layout/SpacerLayout';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { McdInvStatusDefinitionsDialog } from '@/sc/entities/mcdInv/dashboard/McdInvStatusDefinitionsDialog';
import { McdInvSlaCartesianChart } from '@/sc/entities/mcdInv/dashboard/inv/McdInvSlaCartesianChart';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box, Checkbox, ListItemText, MenuItem, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { McdAnalystStateSelector } from '../McdAnalystStateSelector';
import { McdInvPriorQtrPercentCompleteChart } from './McdInvPriorQtrPercentCompleteChart';
import { McdInvPrioritizationGrid } from './McdInvPrioritizationGrid';
import { McdInvProgressChart } from './McdInvProgressChart';
import { McdStlmntProgressChart } from './McdPymtProgressChart';

export const McdInvDashboard = () => {
  const rsrc = useRsrc();

  const { openDialog } = useDialog();
  const { navState, assign } = useNavState();
  const dashboardNavState = navState['mcdInvDashboardState'] ?? {};
  const initNavStateRef = useRef(dashboardNavState);

  const [periods, setPeriods] = useState();
  const [selectedPeriod, setSelectedPeriod] = useState();

  const [selectedStates, setSelectedStates] = useState([]);
  const [openGroups, setOpenGroups] = useState({});

  const [ctrts, setCtrts] = useState();
  const [selectedCtrts, setSelectedCtrts] = useState([]);

  const [dueDates, setDueDates] = useState();
  const [selectedDueDate, setSelectedDueDate] = useState();

  const [mcdInvOrigins, setMcdInvOrigins] = useState();
  const [selectedMcdInvOrigins, setSelectedMcdInvOrigins] = useState([]);

  const [exportReq, setExportReq] = useState();

  useEffect(() => {
    rsrc.data('sc', 'MCD_INV_PERIOD_FILTER_DATA_PLUGIN', {}).then((periods) => {
      setPeriods(periods);
      // Restore nav state
      if (dashboardNavState.selectedPeriodId) {
        const period = periods.find((p) => p.id === dashboardNavState.selectedPeriodId);
        if (period) setSelectedPeriod(period);
      }
      // or set to first period
      else if (periods?.length > 0) {
        const selectedPeriod = periods[0];
        setSelectedPeriod(selectedPeriod);
      }
    });

    rsrc
      .rows('md', 'CTRT', { expr: ExprUtils.whereParams('type', 'MCD'), sortModel: [{ colId: 'name', sort: 'asc' }] })
      .then((ctrts) => {
        setCtrts(ctrts);
        // Restore nav state
        if (dashboardNavState.selectedCtrtCodes) {
          const matchedCtrts = ctrts.filter((ctrt) => dashboardNavState.selectedCtrtCodes.includes(ctrt.code));
          if (matchedCtrts.length > 0) setSelectedCtrts(matchedCtrts);
        }
      });

    rsrc.data('sc', 'MCD_INV_ORIGIN_DATA_PLUGIN').then((mcdInvOrigins) => {
      setMcdInvOrigins(mcdInvOrigins);
      // Restore nav state
      if (dashboardNavState.selectedMcdInvOrigins) {
        const origins = mcdInvOrigins.filter((o) => dashboardNavState.selectedMcdInvOrigins.includes(o.id));
        if (origins.length > 0) setSelectedMcdInvOrigins(origins);
      } else {
        setSelectedMcdInvOrigins([mcdInvOrigins[0]]);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedPeriod?.id) {
      return;
    }
    rsrc
      .data('sc', 'MCD_INV_CHECK_RUN_DATE_FILTER_DATA_PLUGIN', { selectedPeriodId: selectedPeriod.id })
      .then((dates) => {
        setDueDates(dates);
        // Restore nav state
        if (initNavStateRef.current.selectedDueDateEndDate) {
          const dueDate = dates.find((d) => d.endDate === initNavStateRef.current.selectedDueDateEndDate);
          if (dueDate) {
            setSelectedDueDate(dueDate);
          }
        }
      });
  }, [selectedPeriod]);

  const openDefinitionsDialog = () => {
    openDialog(McdInvStatusDefinitionsDialog);
  };

  useEffect(() => {
    const newDashboardNavState = {
      selectedPeriodId: selectedPeriod?.id,
      selectedStateCodes: selectedStates.map((s) => s.code),
      selectedCtrtCodes: selectedCtrts.map((c) => c.code),
      selectedDueDateEndDate: selectedDueDate?.endDate,
      openGroups,
    };

    assign('mcdInvDashboardState', newDashboardNavState);
  }, [selectedPeriod, selectedStates, selectedCtrts, selectedDueDate, openGroups]);

  useEffect(() => {
    setExportReq({
      uiType: 'DASHBOARD',
      entity: 'MCD_INV',
      ctrtIds: selectedCtrts?.map((c) => c.id),
      stateIds: selectedStates?.map((s) => s.id),
      periodId: selectedPeriod?.id,
      startDate: selectedDueDate?.startDate,
      endDate: selectedDueDate?.endDate,
    });
  }, [selectedPeriod, selectedStates, selectedCtrts, selectedDueDate]);

  return (
    <ContentBox>
      {/* Title */}

      <Title
        icon={fas.faTasks}
        title="Medicaid Invoice Dashboard"
        buttons={[
          <TitleButton
            key="comparePolFormulaButton"
            icon={fas.faInfoCircle}
            toolTip="View Definitions"
            onClick={openDefinitionsDialog}
          />,
          <ExportButton exportReq={exportReq} />,
        ]}
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Period */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Invoice Period"
          value={selectedPeriod?.id || ''}
          onChange={(event) => {
            const period = periods.find((p) => p.id === event.target.value);
            setSelectedPeriod(period);
          }}
        >
          {periods?.map((period) => (
            <MenuItem key={period.id} value={period.id}>
              {period.code}
            </MenuItem>
          ))}
        </TextField>

        {/* States */}
        <McdAnalystStateSelector
          onSelectionChange={setSelectedStates}
          onGroupsChange={setOpenGroups}
          selectedStateCodes={dashboardNavState?.selectedStateCodes}
          initOpenGroups={dashboardNavState?.openGroups}
        />

        {/* Contracts */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Contracts"
          value={selectedCtrts}
          onChange={(event) => setSelectedCtrts(event.target.value)}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selected) => (selected.length === 0 ? 'All' : selected.map((p) => p.code).join(', ')),
            MenuProps: {
              MenuListProps: {
                sx: { py: 0 },
              },
            },
          }}
        >
          {ctrts?.map((ctrt) => (
            <MenuItem key={ctrt.code} value={ctrt} dense sx={{ minHeight: 34, py: 0 }}>
              <Checkbox
                size="small"
                sx={{ p: 0.5, mr: 1 }}
                checked={selectedCtrts?.some((p) => p.code === ctrt.code)}
              />
              <ListItemText primary={ctrt.name} primaryTypographyProps={{ fontSize: 14, lineHeight: 1.25 }} />
            </MenuItem>
          ))}
        </TextField>

        {/* Check Run Dates */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Check Run Date"
          value={selectedDueDate || 'NONE'}
          onChange={(event) => setSelectedDueDate(event.target.value === 'NONE' ? null : event.target.value)}
          SelectProps={{
            renderValue: (selected) => (selected === 'NONE' ? 'None' : selected?.displayValue),
          }}
        >
          <MenuItem value="NONE">None</MenuItem>

          {dueDates?.map((dueDate) => (
            <MenuItem key={dueDate.endDate} value={dueDate}>
              {dueDate.displayValue}
            </MenuItem>
          ))}
        </TextField>

        {/* Mcd Inv Origin */}
        <TextField
          fullWidth
          select
          variant="outlined"
          label="Origin"
          value={selectedMcdInvOrigins}
          onChange={(event) => setSelectedMcdInvOrigins(event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.map((o) => o.displayName).join(', '),
          }}
        >
          {mcdInvOrigins?.map((mcdInvOrigin) => (
            <MenuItem key={mcdInvOrigin.name} value={mcdInvOrigin}>
              <Checkbox checked={selectedMcdInvOrigins?.some((o) => o.name === mcdInvOrigin.name)} />
              <ListItemText primary={mcdInvOrigin.displayName} />
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <SpacerLayout>
        <Spacer vertical>
          {/* Row 1 */}
          <Spacer weights={[1, 1, 1.75]}>
            <KrCard title="Invoice Progress" sx={{ flex: 1 }} variant="soft">
              <McdInvProgressChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
                selectedMcdInvOrigins={selectedMcdInvOrigins}
              />
            </KrCard>

            <KrCard title="Settlement Progress" sx={{ flex: 1 }} variant="soft">
              <McdStlmntProgressChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
              />
            </KrCard>

            <KrCard title="Prioritization Grid" sx={{ height: '100%' }} variant="soft">
              <McdInvPrioritizationGrid
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
                selectedMcdInvOrigins={selectedMcdInvOrigins}
              />
            </KrCard>
          </Spacer>

          {/* Row 2 */}
          <Spacer weights={[1, 1, 1.75]}>
            <KrCard title="CLD Progress" sx={{ height: '100%' }} variant="soft">
              <McdInvProgressChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
                selectedMcdInvOrigins={selectedMcdInvOrigins}
                isCld
              />
            </KrCard>

            <KrCard title="Prior Quarter % Completion" sx={{ height: '100%' }} variant="soft">
              <McdInvPriorQtrPercentCompleteChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
              />
            </KrCard>

            <KrCard title="SLA Tracker" sx={{ height: '100%' }} variant="soft">
              <McdInvSlaCartesianChart
                selectedStates={selectedStates}
                selectedCtrts={selectedCtrts}
                selectedPeriod={selectedPeriod}
                selectedDueDate={selectedDueDate}
              />
            </KrCard>
          </Spacer>
        </Spacer>
      </SpacerLayout>
    </ContentBox>
  );
};
